import { computed, ref } from 'vue'
import serviceAi from '@/services/serviceAi'
import { getPermissao } from '@/services/permissao-service'
import { canAccessThalamusAi } from '@/utils/ai-assistant.access'
import { createPageContextRegistry } from '@/utils/page-context.registry'
import {
    normalizeAssistantPatches,
    projectDraftToPatches,
    isAssistantComposerLocked,
    normalizeAssistantState,
    resolveAssistantPresentation,
    resolveTransientActivity,
    shouldApplyCommittedDraft,
} from '@/utils/ai-assistant.state'
import {
    assistantQueueStatus,
    confirmOptimisticMessage,
    createClientMessageId,
    reconcileServerMessages,
    splitAssistantMessages,
} from '@/utils/ai-assistant.messages'
import { buildAssistantActionPreview } from '@/utils/pmo-agent.presentation'
import { getWebSocketService } from '@/services/websocketservice'
import {
    ASSISTANT_FALLBACK_POLL_MS,
    assistantChannel,
    parseAssistantTurnNotice,
} from '@/utils/ai-assistant.realtime'

const POLL_INTERVAL_MS = ASSISTANT_FALLBACK_POLL_MS
const MAX_POLL_ERRORS = 3
// Conversa parada por mais que esta janela não é retomada: o contexto antigo
// voltaria ao prompt sem utilidade e encareceria o turno.
const RESUME_WINDOW_MS = 30 * 60 * 1000
const ACTIVE_STATUSES = new Set(['queued', 'running', 'processing'])
const registry = createPageContextRegistry()
const registryRevision = ref(0)
const drawerOpen = ref(false)
const sessionId = ref(null)
const invocationId = ref(null)
const messages = ref([])
const processing = ref(false)
const polling = ref(false)
const progress = ref({})
const sources = ref([])
const findings = ref([])
const actionPreview = ref(null)
const skillState = ref('')
const assistantState = ref(normalizeAssistantState())
const currentClarification = ref(null)
const maturity = ref(null)
const activities = ref([])
const failure = ref(null)
const error = ref('')
const conversations = ref([])
const conversationsLoading = ref(false)
const conversationSearch = ref('')
const historyOpen = ref(false)
const currentTitle = ref('Nova conversa')
const appliedDraftRevisions = new Map()
const hasAiFunctionality = ref(false)
let pollTimer = null
let lastAppliedActionIds = ''
let sessionPromise = null
let enqueueChain = Promise.resolve()
let pendingSendRequests = 0
let pollErrorCount = 0
let activePageIdentity = null
let lastInteractionAt = 0

function unwrap(response) {
    return response?.data?.data || response?.data || response || {}
}

function firstId(data, keys) {
    for (const key of keys) {
        if (data?.[key]) return data[key]
    }
    return null
}

function resolveSources(data, pmo, current) {
    const candidates = [data?.sources, pmo?.sources, data?.evidence, pmo?.evidence]
    const populated = candidates.find((item) => Array.isArray(item) && item.length)
    if (populated) return populated
    const empty = candidates.find((item) => Array.isArray(item))
    return empty ?? current
}

function currentContext() {
    registryRevision.value
    return registry.currentPageContext()
}

function contextPayload(context = currentContext()) {
    if (!context) return null
    const dynamic = typeof context.getContext === 'function' ? context.getContext() : {}
    return {
        ...dynamic,
        module: context.module,
        route: context.route,
        entity_type: context.entityType || null,
        entity_id: context.entityId || null,
        mode: context.mode,
        capabilities: context.capabilities || [],
        page_identity: context.pageIdentity,
    }
}

function isProjectWorkContext(context = currentContext()) {
    return context?.capabilities?.includes('project_work')
}

function maybeRefreshWork(data) {
    const context = currentContext()
    if (!isProjectWorkContext(context) || typeof context.refreshWork !== 'function') return
    const actions = data?.applied_actions || data?.snapshot?.applied_actions || []
    if (!Array.isArray(actions) || actions.length === 0) return
    const signature = actions.map((action) => action?.id).filter(Boolean).join(',')
    if (!signature || signature === lastAppliedActionIds) return
    lastAppliedActionIds = signature
    context.refreshWork()
}

function applyPayload(data) {
    const context = currentContext()
    if (!context || typeof context.applyPatches !== 'function') return
    const revision = Number(
        data.context_revision
        ?? data.invocation?.context_revision
        ?? contextPayload(context)?.context_revision
        ?? 0,
    )
    const latestInvocation = Array.isArray(data.invocations) ? data.invocations.at(-1) : null
    const draftKey = latestInvocation?.id || data.invocation_id || 'current'
    const lastAppliedRevision = appliedDraftRevisions.get(draftKey) || 0
    const committedRevision = Number(latestInvocation?.committed_draft_revision || 0)
    const committedPatches = shouldApplyCommittedDraft(lastAppliedRevision, committedRevision)
        ? normalizeAssistantPatches({ patches: latestInvocation?.deep_patches }, revision)
        : []
    const patches = [
        ...normalizeAssistantPatches(data, revision),
        ...committedPatches,
    ]
    const draftPatches = patches.length ? [] : projectDraftToPatches(
        data.draft || data.snapshot?.draft,
        revision,
    )
    if (patches.length || draftPatches.length) context.applyPatches([...patches, ...draftPatches])
    if (committedPatches.length || shouldApplyCommittedDraft(lastAppliedRevision, committedRevision)) {
        appliedDraftRevisions.set(draftKey, committedRevision)
    }
}

function mergeSnapshot(response, { optimisticClientId = null } = {}) {
    const data = unwrap(response)
    const latestInvocation = Array.isArray(data.invocations) ? data.invocations.at(-1) : null
    const pmo = latestInvocation?.pmo || data.pmo || data.snapshot
    sessionId.value = firstId(data, ['session_id', 'session_uuid', 'uuid'])
        || data.session?.uuid || data.session?.id || data.id || sessionId.value
    currentTitle.value = data.title || currentTitle.value
    invocationId.value = firstId(data, ['invocation_id', 'invocation_uuid'])
        || data.invocation?.uuid || data.invocation?.id || latestInvocation?.id || invocationId.value
    progress.value = data.progress || data.invocation?.progress || pmo?.progress || progress.value
    sources.value = resolveSources(data, pmo, sources.value)
    findings.value = data.findings || pmo?.findings || findings.value
    skillState.value = data.skill_state || latestInvocation?.skill_state || pmo?.progress?.phase || skillState.value
    currentClarification.value = latestInvocation?.current_clarification || null
    maturity.value = latestInvocation?.maturity || maturity.value
    activities.value = latestInvocation?.activities || activities.value
    failure.value = latestInvocation?.failure || null
    const latestMessageState = Array.isArray(data.messages)
        ? [...data.messages].reverse().find((item) => item?.role === 'assistant')?.payload?.assistant_state
        : null
    assistantState.value = normalizeAssistantState(data.assistant_state || latestMessageState)
    const fallbackPreview = latestInvocation?.skill_state === 'ready' && latestInvocation?.maturity?.ready
        && latestInvocation?.deep_patches?.length ? {
        title: 'Refinamento PMO disponível',
        description: 'Revise no formulário e confirme as sugestões que deseja manter.',
    } : null
    actionPreview.value = isProjectWorkContext() ? null : buildAssistantActionPreview({
        actionPreview: data.action_preview,
        draft: data.draft || data.snapshot?.draft || pmo?.draft,
        fieldProvenance: data.field_provenance || pmo?.field_provenance || {},
        fallback: fallbackPreview,
    })
    applyPayload(data)
    maybeRefreshWork(data)

    if (Array.isArray(data.messages)) {
        messages.value = reconcileServerMessages(messages.value, data.messages)
    }
    if (optimisticClientId && data.user_message_id) {
        confirmOptimisticMessage(
            messages.value,
            optimisticClientId,
            data.user_message_id,
            data.queue_status,
        )
    }
    const assistantMessage = data.reply || data.message?.message || data.message?.content
        || data.assistant_message || data.fast_turn?.reply
        || (typeof data.message === 'string' ? data.message : '')
    const assistantServerId = data.message_id || data.message?.id || null
    if (assistantMessage && !messages.value.some((item) =>
        assistantServerId ? item.serverId === assistantServerId : item.role === 'assistant' && item.message === assistantMessage
    )) {
        messages.value.push({
            role: 'assistant',
            message: assistantMessage,
            serverId: assistantServerId,
            clientId: assistantServerId ? null : createClientMessageId(),
            status: 'sent',
            createdAt: new Date().toISOString(),
            payload: {
                manager: data.manager || null,
                sources: data.sources || [],
                assistant_state: data.assistant_state || null,
            },
        })
    }
    return data
}

function stopPolling() {
    if (pollTimer) clearTimeout(pollTimer)
    pollTimer = null
    polling.value = false
}

function resetAssistantSession() {
    stopPolling()
    sessionId.value = null
    invocationId.value = null
    messages.value = []
    processing.value = false
    progress.value = {}
    sources.value = []
    findings.value = []
    actionPreview.value = null
    skillState.value = ''
    assistantState.value = normalizeAssistantState()
    currentClarification.value = null
    maturity.value = null
    activities.value = []
    failure.value = null
    error.value = ''
    currentTitle.value = 'Nova conversa'
    appliedDraftRevisions.clear()
    lastAppliedActionIds = ''
    sessionPromise = null
    enqueueChain = Promise.resolve()
    pendingSendRequests = 0
    pollErrorCount = 0
    lastInteractionAt = 0
}

function isWithinResumeWindow(value) {
    const timestamp = value ? new Date(value).getTime() : 0
    return Number.isFinite(timestamp) && timestamp > 0
        && Date.now() - timestamp <= RESUME_WINDOW_MS
}

function discardIdleSession() {
    if (!sessionId.value) return
    if (lastInteractionAt && Date.now() - lastInteractionAt <= RESUME_WINDOW_MS) return
    resetAssistantSession()
}

async function openDrawer() {
    drawerOpen.value = true
    discardIdleSession()
    await refreshConversations({ selectLatest: true })
}

async function refreshConversations({ selectLatest = false, q = conversationSearch.value } = {}) {
    conversationsLoading.value = true
    try {
        const response = await serviceAi.listAssistantSessions({ per_page: 50, q })
        const data = response?.data?.data || response?.data || []
        conversations.value = Array.isArray(data) ? data : []
        const [latest] = conversations.value
        if (selectLatest && !sessionId.value && latest
            && isWithinResumeWindow(latest.last_activity_at)) {
            await loadConversation(latest.id)
        }
    } catch (historyError) {
        error.value = historyError?.response?.data?.message || 'Não foi possível carregar as conversas.'
    } finally {
        conversationsLoading.value = false
    }
}

async function loadConversation(id) {
    if (!id) return
    historyOpen.value = false
    if (id === sessionId.value) return
    stopPolling()
    resetAssistantSession()
    processing.value = true
    try {
        const response = await serviceAi.getAssistantSession(id)
        const data = mergeSnapshot(response)
        lastInteractionAt = Date.now()
        if (hasQueuedTurns(data) || ACTIVE_STATUSES.has(deepStatus(data))) startPolling()
    } catch (loadError) {
        error.value = loadError?.response?.data?.message || 'Não foi possível abrir a conversa.'
    } finally {
        processing.value = false
    }
}

async function newConversation() {
    resetAssistantSession()
    historyOpen.value = false
    await ensureSession()
    lastInteractionAt = Date.now()
    await refreshConversations()
}

async function searchConversations(query) {
    conversationSearch.value = String(query || '').trim()
    await refreshConversations()
}

async function renameConversation({ id = sessionId.value, title } = {}) {
    const normalized = String(title || '').trim()
    if (!id || !normalized) return
    const response = await serviceAi.updateAssistantSession(id, { title: normalized })
    if (id === sessionId.value) mergeSnapshot(response)
    await refreshConversations()
}

async function archiveConversation(id = sessionId.value) {
    if (!id) return
    await serviceAi.updateAssistantSession(id, { archived: true })
    if (id === sessionId.value) resetAssistantSession()
    await refreshConversations({ selectLatest: true })
}

function deepStatus(data) {
    const latestInvocation = Array.isArray(data.invocations) ? data.invocations.at(-1) : null
    return String(
        latestInvocation?.pmo?.run?.status
        || latestInvocation?.pmo?.status
        || data.deep_run?.status
        || data.invocation?.status
        || data.deep_pmo?.status
        || data.run?.status
        || data.status
        || '',
    ).toLowerCase()
}

function hasQueuedTurns(data = {}) {
    if (['queued', 'processing'].includes(data.queue_status)) return true
    return Array.isArray(data.messages) && data.messages.some((item) =>
        ['queued', 'processing'].includes(item?.payload?.queue?.status),
    )
}

async function pollDeepRun() {
    if (!sessionId.value) return
    polling.value = true
    try {
        const response = await serviceAi.getAssistantInvocationProgress(sessionId.value)
        const data = mergeSnapshot(response)
        pollErrorCount = 0
        if (!hasQueuedTurns(data) && !ACTIVE_STATUSES.has(deepStatus(data))) {
            stopPolling()
            return
        }
    } catch (pollError) {
        pollErrorCount += 1
        error.value = pollError?.response?.data?.message || pollError?.message || 'Falha ao atualizar a análise.'
        if (pollErrorCount >= MAX_POLL_ERRORS) {
            stopPolling()
            return
        }
    }
    pollTimer = setTimeout(
        pollDeepRun,
        POLL_INTERVAL_MS * Math.max(1, pollErrorCount),
    )
}

let assistantRealtimeBound = false

function onAssistantRealtime(event) {
    const notice = parseAssistantTurnNotice(event)
    if (!notice || String(notice.sessionId) !== String(sessionId.value)) return
    if (pollTimer) clearTimeout(pollTimer)
    pollDeepRun()
}

function bindAssistantRealtime() {
    if (assistantRealtimeBound) return
    assistantRealtimeBound = true
    getWebSocketService().addListener(onAssistantRealtime)
}

function startPolling() {
    if (!sessionId.value) return
    bindAssistantRealtime()
    getWebSocketService().subscribe(assistantChannel(sessionId.value))
    if (pollTimer) clearTimeout(pollTimer)
    pollErrorCount = 0
    polling.value = true
    pollTimer = setTimeout(pollDeepRun, POLL_INTERVAL_MS)
}

async function ensureSession() {
    if (sessionId.value) return
    if (!sessionPromise) {
        sessionPromise = serviceAi.createAssistantSession(contextPayload())
            .then((response) => {
                mergeSnapshot(response)
                if (!sessionId.value) throw new Error('A API não retornou o identificador da sessão.')
                refreshConversations()
            })
            .finally(() => {
                sessionPromise = null
            })
    }
    await sessionPromise
}

async function sendMessage(input) {
    const message = String((typeof input === 'object' ? input?.message : input) || '').trim()
    const attachments = Array.isArray(input?.attachments) ? input.attachments : []
    const context = currentContext()
    if ((!message && !attachments.length) || !context) return false
    const effectiveMessage = message || 'Analise o PDF anexado.'
    const sessionBusy = messages.value.some((item) =>
        item.role === 'user' && ['processing', 'queued'].includes(assistantQueueStatus(item)))
    const optimistic = {
        role: 'user',
        message: effectiveMessage,
        attachments: attachments.map((file) => ({
            name: file.name,
            mime_type: file.type || 'application/pdf',
            size: file.size,
            status: 'ready',
        })),
        clientId: createClientMessageId(),
        serverId: null,
        status: sessionBusy ? 'queued' : 'processing',
        createdAt: new Date().toISOString(),
    }
    messages.value.push(optimistic)
    pendingSendRequests += 1
    processing.value = true
    lastInteractionAt = Date.now()
    error.value = ''
    try {
        await ensureSession()
        const request = enqueueChain.then(() => serviceAi.sendAssistantMessage(
            sessionId.value,
            effectiveMessage,
            contextPayload(context),
            attachments,
        ))
        enqueueChain = request.catch(() => undefined)
        const response = await request
        const data = mergeSnapshot(response, { optimisticClientId: optimistic.clientId })
        if (hasQueuedTurns(data) || ACTIVE_STATUSES.has(deepStatus(data))) startPolling()
        return true
    } catch (sendError) {
        optimistic.status = 'error'
        error.value = sendError?.response?.data?.message
            || sendError?.response?.data?.error
            || sendError?.message
            || 'Não foi possível enviar a mensagem.'
        return false
    } finally {
        pendingSendRequests = Math.max(0, pendingSendRequests - 1)
        processing.value = pendingSendRequests > 0
    }
}

async function retryInvocation() {
    if (!sessionId.value || !invocationId.value || !failure.value?.retryable) return
    processing.value = true
    error.value = ''
    try {
        const response = await serviceAi.retryAssistantInvocation(sessionId.value, invocationId.value)
        const data = mergeSnapshot(response)
        if (ACTIVE_STATUSES.has(deepStatus(data))) startPolling()
    } catch (retryError) {
        error.value = retryError?.response?.data?.message || retryError?.message || 'Não foi possível tentar novamente.'
    } finally {
        processing.value = false
    }
}

async function confirmInvocation() {
    if (!sessionId.value || !invocationId.value) return
    processing.value = true
    error.value = ''
    try {
        const response = await serviceAi.confirmAssistantInvocation(sessionId.value, invocationId.value)
        mergeSnapshot(response)
        currentContext()?.confirmSuggestions?.()
        actionPreview.value = null
    } catch (confirmError) {
        error.value = confirmError?.response?.data?.message
            || confirmError?.message
            || 'Não foi possível confirmar as sugestões.'
    } finally {
        processing.value = false
    }
}

async function cancelInvocation() {
    if (!sessionId.value || !invocationId.value) return
    processing.value = true
    error.value = ''
    try {
        const response = await serviceAi.cancelAssistantInvocation(sessionId.value, invocationId.value)
        mergeSnapshot(response)
        stopPolling()
        actionPreview.value = null
    } catch (cancelError) {
        error.value = cancelError?.response?.data?.message
            || cancelError?.message
            || 'Não foi possível cancelar a execução.'
    } finally {
        processing.value = false
    }
}

async function refreshAiAccess() {
    hasAiFunctionality.value = canAccessThalamusAi(await getPermissao())
}

export function useAiAssistantContext() {
    refreshAiAccess()
    const activeContext = computed(() => currentContext())
    const available = computed(() =>
        hasAiFunctionality.value && !!activeContext.value,
    )
    const composerLocked = computed(() => isAssistantComposerLocked(
        assistantState.value.mode === 'chat' ? '' : skillState.value,
        processing.value,
    ))
    const currentActivity = computed(() => {
        if (assistantState.value.activityLabel) {
            return { label: assistantState.value.activityLabel, detail: '' }
        }
        const processingTurn = messages.value.find((message) =>
            message.role === 'user' && message.status === 'processing')
        const queueLabel = processingTurn?.payload?.queue?.activity_label
        if (queueLabel) {
            return { label: queueLabel, detail: '' }
        }
        if (assistantState.value.mode === 'chat') {
            return null
        }

        return resolveTransientActivity(
                activities.value,
                skillState.value,
                processing.value || polling.value,
            )
    })
    const assistantPresentation = computed(() => resolveAssistantPresentation(
        assistantState.value,
        activeContext.value,
        Boolean(currentClarification.value),
    ))
    const messageGroups = computed(() => splitAssistantMessages(messages.value))
    const queuedMessages = computed(() => messageGroups.value.queued)
    const transcriptMessages = computed(() => messageGroups.value.transcript)

    function registerPageContext(context) {
        if (activePageIdentity !== null && activePageIdentity !== context?.pageIdentity) {
            resetAssistantSession()
        }
        activePageIdentity = context?.pageIdentity || null
        const cleanupRegistry = registry.registerPageContext(context)
        registryRevision.value += 1
        return () => {
            cleanupRegistry()
            registryRevision.value += 1
        }
    }

    return {
        activeContext,
        available,
        drawerOpen,
        sessionId,
        invocationId,
        messages,
        queuedMessages,
        transcriptMessages,
        processing,
        polling,
        progress,
        sources,
        findings,
        actionPreview,
        skillState,
        assistantState,
        assistantPresentation,
        currentClarification,
        maturity,
        activities,
        failure,
        composerLocked,
        currentActivity,
        error,
        conversations,
        conversationsLoading,
        conversationSearch,
        historyOpen,
        currentTitle,
        registerPageContext,
        open: openDrawer,
        close: () => { drawerOpen.value = false },
        toggle: () => {
            if (drawerOpen.value) {
                drawerOpen.value = false
                return
            }
            openDrawer()
        },
        sendMessage,
        refreshConversations,
        searchConversations,
        loadConversation,
        newConversation,
        renameConversation,
        archiveConversation,
        confirmInvocation,
        cancelInvocation,
        retryInvocation,
        stopPolling,
    }
}
